import { getLanguageVariants } from "@/config/lang";

/**
 * 获取当前语言设置和对应的accept-language头信息
 * @returns 包含acceptLanguage和acceptLanguageHeader的对象
 */
export function getAcceptLanguageConfig() {
  // 默认语言设置
  let acceptLanguage = "zh-CN";

  // 尝试从 localStorage 获取用户首选语言
  try {
    const lang = localStorage.getItem("responsive-locale");
    if (lang) {
      const langObj = JSON.parse(lang);
      acceptLanguage = langObj?.locale || "zh-CN";
    }
  } catch (error) {
    console.debug("多语言配置获取失败，使用默认值");
  }

  // 获取语言变体配置
  const languageVariants = {
    zh: ["zh-CN,zh;q=0.9,en;q=0.8"],
    en: ["en-US,en;q=0.9,zh-CN;q=0.8"],
    ...getLanguageVariants()
  };

  // 构建标准的accept-language头
  const acceptLanguageHeader =
    languageVariants[acceptLanguage]?.[0] ||
    `${acceptLanguage};q=1.0,en;q=0.9,zh-CN;q=0.8`;

  return {
    acceptLanguage, // 简单的语言代码，用于X-Language头
    acceptLanguageHeader // 完整的accept-language头，包含权重
  };
}

/**
 * 创建带语言设置的WebSocket连接
 * WebSocket协议本身不支持直接设置自定义头部，所以采用URL参数方式传递语言信息
 * @param wsUrl WebSocket连接地址
 * @returns 配置好的WebSocket实例
 */
export function createWebSocketWithLanguage(wsUrl: string): WebSocket {
  // 获取语言配置
  const { acceptLanguage } = getAcceptLanguageConfig();

  // 在URL中添加语言参数
  // 检查URL是否已经包含查询参数
  const separator = wsUrl.includes("?") ? "&" : "?";
  const urlWithLang = `${wsUrl}${separator}lang=${encodeURIComponent(acceptLanguage)}`;

  // 创建WebSocket连接
  const ws = new WebSocket(urlWithLang);

  // 注意：WebSocket协议本身不支持自定义头部
  // 如果需要在握手阶段设置头部，需要使用WebSocket构造函数的可选参数
  // 但这在不同浏览器中的支持可能不一致
  // 以下代码仅在支持的环境中生效
  // const ws = new WebSocket(urlWithLang, [], {
  //   headers: {
  //     'Accept-Language': acceptLanguageHeader,
  //     'X-Language': acceptLanguage
  //   }
  // });

  return ws;
}

/**
 * 为现有WebSocket连接发送语言信息
 * 在WebSocket连接建立后，可以通过发送一条特殊的消息来传递语言信息
 * @param ws WebSocket实例
 * @param messageType 消息类型标识，默认为'language-info'
 */
export function sendLanguageInfo(
  ws: WebSocket,
  messageType: string = "language-info"
): void {
  if (ws.readyState === WebSocket.OPEN) {
    const { acceptLanguage } = getAcceptLanguageConfig();

    // 发送语言信息作为第一条消息
    ws.send(
      JSON.stringify({
        type: messageType,
        language: acceptLanguage
      })
    );
  }
}
